import React, { useState } from "react";
import {
  Popover,
  Button,
  Box,
  Typography,
  IconButton,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const STATUS_OPTIONS = ["In_Transit", "In_Building", "In_Room", "Picked", "Transfered"];
const TYPE_OPTIONS = ["Move", "Receive"];

interface TransfersFilterDialogProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onApply: (filters: any) => void;
}

const TransfersFilterDialog = ({ open, anchorEl, onClose, onApply }: TransfersFilterDialogProps) => {
  const [activeFilter, setActiveFilter] = useState("Status");
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);

  const handleStatusToggle = (status: string) => {
    setSelectedStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  const handleTypeToggle = (type: string) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleSelectAll = () => {
    if (activeFilter === "Status") {
      setSelectedStatuses(selectedStatuses.length === STATUS_OPTIONS.length ? [] : [...STATUS_OPTIONS]);
    } else if (activeFilter === "Type") {
      setSelectedTypes(selectedTypes.length === TYPE_OPTIONS.length ? [] : [...TYPE_OPTIONS]);
    }
  };

  const handleClearAll = () => {
    setSelectedStatuses([]);
    setSelectedTypes([]);
  };

  const handleApply = () => {
    onApply({ statuses: selectedStatuses, types: selectedTypes });
  };

  const renderRightContent = () => {
    if (activeFilter === "Status") {
      return (
        <Box sx={{ border: "1px solid", borderColor: "grey.300", borderRadius: "6px", p: 1.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedStatuses.length === STATUS_OPTIONS.length}
                indeterminate={selectedStatuses.length > 0 && selectedStatuses.length < STATUS_OPTIONS.length}
                onChange={handleSelectAll}
                sx={{ color: "grey.primary", "&.Mui-checked": { color: "primary.main" }, "&.MuiCheckbox-indeterminate": { color: "primary.main" } }}
              />
            }
            label={<Typography variant="body2">Select All</Typography>}
            sx={{ mb: 1 }}
          />
          <Divider sx={{ mb: 1 }} />
          <Box sx={{ maxHeight: "180px", overflowY: "auto", "&::-webkit-scrollbar": { display: "none" }, msOverflowStyle: "none", scrollbarWidth: "none" }}>
            <FormGroup>
              {STATUS_OPTIONS.map((status) => (
                <FormControlLabel
                  key={status}
                  control={
                    <Checkbox
                      checked={selectedStatuses.includes(status)}
                      onChange={() => handleStatusToggle(status)}
                      sx={{ color: "grey.primary", "&.Mui-checked": { color: "primary.main" } }}
                    />
                  }
                  label={<Typography variant="body2">{status.replace(/_/g, " ")}</Typography>}
                />
              ))}
            </FormGroup>
          </Box>
        </Box>
      );
    } else if (activeFilter === "Type") {
      return (
        <Box sx={{ border: "1px solid", borderColor: "grey.300", borderRadius: "6px", p: 1.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedTypes.length === TYPE_OPTIONS.length}
                indeterminate={selectedTypes.length > 0 && selectedTypes.length < TYPE_OPTIONS.length}
                onChange={handleSelectAll}
                sx={{ color: "grey.primary", "&.Mui-checked": { color: "primary.main" }, "&.MuiCheckbox-indeterminate": { color: "primary.main" } }}
              />
            }
            label={<Typography variant="body2">Select All</Typography>}
            sx={{ mb: 1 }}
          />
          <Divider sx={{ mb: 1 }} />
          <Box sx={{ maxHeight: "180px", overflowY: "auto", "&::-webkit-scrollbar": { display: "none" }, msOverflowStyle: "none", scrollbarWidth: "none" }}>
            <FormGroup>
              {TYPE_OPTIONS.map((type) => (
                <FormControlLabel
                  key={type}
                  control={
                    <Checkbox
                      checked={selectedTypes.includes(type)}
                      onChange={() => handleTypeToggle(type)}
                      sx={{ color: "grey.primary", "&.Mui-checked": { color: "primary.main" } }}
                    />
                  }
                  label={<Typography variant="body2">{type}</Typography>}
                />
              ))}
            </FormGroup>
          </Box>
        </Box>
      );
    }
    return null;
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{ sx: { borderRadius: "0.75rem", width: "450px", mt: 1 } }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 1.5, pb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>Filter</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      
      <Divider />

      <Box sx={{ p: 0, display: "flex", height: "280px", backgroundColor: "background.default" }}>
        <Box sx={{ width: "40%", borderRight: "1px solid", borderColor: "grey.300", p: 1.5, backgroundColor: "background.default" }}>
          {["Status", "Type"].map((filter) => (
            <Box
              key={filter}
              onClick={() => setActiveFilter(filter)}
              sx={{
                p: 1,
                cursor: "pointer",
                borderRadius: "6px",
                mb: 0.5,
                backgroundColor: activeFilter === filter ? "white" : "transparent",
                color: activeFilter === filter ? "primary.main" : "text.primary",
                fontWeight: activeFilter === filter ? 600 : 400,
              }}
            >
              <Typography variant="body2">{filter}</Typography>
            </Box>
          ))}
        </Box>
        
        <Box sx={{ width: "60%", p: 1.5, backgroundColor: "white" }}>
          {renderRightContent()}
        </Box>
      </Box>

      <Divider />

      <Box sx={{ px: 2, py: 1.5, display: "flex", justifyContent: "space-between", backgroundColor: "background.default" }}>
        <Button onClick={handleClearAll} sx={{ textTransform: "none", color: "text.primary", "&:hover": { backgroundColor: "transparent" } }}>
          Clear all
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" onClick={onClose} sx={{ borderRadius: "6px", textTransform: "none", borderColor: "grey.300", color: "text.primary", backgroundColor: "#fff", "&:hover": { backgroundColor: "#fff", borderColor: "grey.300" } }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleApply} sx={{ borderRadius: "6px", textTransform: "none", backgroundColor: "primary.main", "&:hover": { backgroundColor: "primary.main" } }}>
            OK
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};
export default TransfersFilterDialog;
